// src/services/bluesky.js
import { BskyAgent, RichText } from '@atproto/api';
import { Capacitor } from '@capacitor/core';
import { API_URL, BUNDLED_CREDENTIALS, hasBundledCredentials } from '../config/native';

const BLUESKY_SERVICE = 'https://bsky.social';

// Determine if we're running in a native Capacitor app
const isNative = Capacitor.isNativePlatform();

// API endpoint for serverless functions
// - In native apps: use the remote Vercel API
// - On web (Vercel): use relative path
// - In development: fall back to direct Bluesky access
const API_BASE = isNative 
  ? `${API_URL}/api` 
  : (process.env.NODE_ENV === 'production' ? '/api' : '/api');

// Flag to track if we should use serverless functions
// Use serverless in production web OR in native apps (if API is available)
const USE_SERVERLESS = process.env.NODE_ENV === 'production' || isNative;

export class BlueskyService {
  constructor() {
    this.agent = new BskyAgent({ service: BLUESKY_SERVICE });
    this.hasPublicSession = false;
  }

  // Create a public session using serverless function (secure)
  async createPublicSession() {
    // In production or native, use the serverless function
    if (USE_SERVERLESS) {
      try {
        const response = await fetch(`${API_BASE}/auth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getPublicSession' }),
        });

        if (!response.ok) {
          const error = await response.json();
          return { success: false, error: error.message || 'Failed to get public session' };
        }

        const data = await response.json();
        if (data.success && data.session?.accessJwt) {
          // Create a new agent and set the session
          this.agent = new BskyAgent({ service: BLUESKY_SERVICE });
          // We can't fully resume without refreshJwt, but we can set the header
          this.agent.api.setHeader('Authorization', `Bearer ${data.session.accessJwt}`);
          this.hasPublicSession = true;
          return { success: true, data: data.session };
        }

        return { success: false, error: 'Invalid session response' };
      } catch (error) {
        // Serverless auth failed, falling back to bundled credentials
        // Fall through to direct access as backup
      }
    }

    // Fall back to bundled credentials (for native apps or dev mode)
    if (hasBundledCredentials()) {
      try {
        this.agent = new BskyAgent({ service: BLUESKY_SERVICE });
        const response = await this.agent.login({
          identifier: BUNDLED_CREDENTIALS.identifier,
          password: BUNDLED_CREDENTIALS.password,
        });
        this.hasPublicSession = true;
        // Using bundled credentials for public session
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: error.message };
      }
    }

    return { success: false, error: 'Public access not configured. Please ensure the API is accessible or bundled credentials are available.' };
  }

  // Fetch feed using serverless function (for unauthenticated users)
  async getFeedViaAPI(feedUri, cursor = null) {
    try {
      const params = new URLSearchParams({ feedUri });
      if (cursor) params.append('cursor', cursor);

      const response = await fetch(`${API_BASE}/feed?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.message || 'Failed to fetch feed' };
      }

      const data = await response.json();
      return {
        success: true,
        data: data.feed,
        cursor: data.cursor,
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Login and return session data for secure storage
  async login(identifier, password, rememberMe = true) {
    try {
      const response = await this.agent.login({
        identifier,
        password,
      });

      // Return session data (tokens) - NOT the password
      const sessionData = {
        did: response.data.did,
        handle: response.data.handle,
        email: response.data.email,
        accessJwt: response.data.accessJwt,
        refreshJwt: response.data.refreshJwt,
        rememberMe
      };

      return {
        success: true,
        data: sessionData
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Resume session using stored JWT tokens
  async resumeSession(sessionData) {
    try {
      if (sessionData?.accessJwt && sessionData?.refreshJwt) {
        // Use the proper resumeSession method with JWT tokens
        await this.agent.resumeSession({
          did: sessionData.did,
          handle: sessionData.handle,
          accessJwt: sessionData.accessJwt,
          refreshJwt: sessionData.refreshJwt,
        });
        return {
          success: true,
          data: sessionData
        };
      }
      return {
        success: false,
        error: 'No valid session tokens found'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get current session info
  getSession() {
    return this.agent.session;
  }

  async getFeed(feedUri, cursor = null, retryCount = 0) {
    // If user is logged in, use their session directly
    if (this.agent.session) {
      try {
        const params = {
          feed: feedUri,
          limit: 20
        };
        if (cursor) params.cursor = cursor;

        const response = await this.agent.app.bsky.feed.getFeed(params);
        return {
          success: true,
          data: response.data.feed,
          cursor: response.data.cursor
        };
      } catch (error) {
        // If session expired, try to create a new public session
        if (error.message?.includes('Authentication') && retryCount < 2) {
          this.agent = new BskyAgent({ service: BLUESKY_SERVICE });
          const sessionResponse = await this.createPublicSession();
          if (sessionResponse.success) {
            return this.getFeed(feedUri, cursor, retryCount + 1);
          }
        }
        return { success: false, error: error.message };
      }
    }

    // For unauthenticated users, try serverless API first
    if (USE_SERVERLESS) {
      const apiResult = await this.getFeedViaAPI(feedUri, cursor);
      if (apiResult.success) {
        return apiResult;
      }
      // If API fails, fall back to creating a public session
      // API fetch failed, trying direct access as fallback
    }

    // Try direct access with public session as fallback
    try {
      // Create public session if we don't have one
      if (!this.hasPublicSession && retryCount < 2) {
        const sessionResponse = await this.createPublicSession();
        if (sessionResponse.success) {
          return this.getFeed(feedUri, cursor, retryCount + 1);
        }
      }

      const params = {
        feed: feedUri,
        limit: 20
      };
      if (cursor) params.cursor = cursor;

      const response = await this.agent.app.bsky.feed.getFeed(params);
      return {
        success: true,
        data: response.data.feed,
        cursor: response.data.cursor
      };
    } catch (error) {
      // If auth error and haven't retried too many times, create public session
      if (error.message?.includes('Authentication Required') && retryCount < 2) {
        const sessionResponse = await this.createPublicSession();
        if (sessionResponse.success) {
          return this.getFeed(feedUri, cursor, retryCount + 1);
        }
      }
      
      return { success: false, error: error.message };
    }
  }

  // Create post with RichText support (auto-detects links, mentions, hashtags)
  async createPost(text) {
    if (!this.agent.session) {
      return {
        success: false,
        error: 'Not logged in',
      };
    }

    try {
      // Use RichText to detect and create facets for links, mentions, hashtags
      const richText = new RichText({ text });
      await richText.detectFacets(this.agent);

      const response = await this.agent.post({
        text: richText.text,
        facets: richText.facets,
        createdAt: new Date().toISOString(),
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  async likePost(uri, cid) {
    if (!this.agent.session) {
      return {
        success: false,
        error: 'Not logged in',
      };
    }

    try {
      const response = await this.agent.like(uri, cid);
      return { 
        success: true,
        data: response // Returns the like record URI
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Unlike requires the like record URI (stored in viewer.like)
  async unlikePost(likeUri) {
    if (!this.agent.session) {
      return {
        success: false,
        error: 'Not logged in',
      };
    }

    try {
      await this.agent.deleteLike(likeUri);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Repost a post
  async repost(uri, cid) {
    if (!this.agent.session) {
      return {
        success: false,
        error: 'Not logged in',
      };
    }

    try {
      const response = await this.agent.repost(uri, cid);
      return { 
        success: true,
        data: response
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Delete a repost
  async deleteRepost(repostUri) {
    if (!this.agent.session) {
      return {
        success: false,
        error: 'Not logged in',
      };
    }

    try {
      await this.agent.deleteRepost(repostUri);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  async getFollowStatus(did) {
	if (!this.agent.session) {
	  return {
		success: false,
		error: 'Not logged in'
	  };
	}
  
	try {
	  const response = await this.agent.api.app.bsky.graph.getFollows({
		actor: this.agent.session.did,
	  });
  
	  const isFollowing = response.data.follows.some(
		follow => follow.did === did
	  );
  
	  return {
		success: true,
		isFollowing
	  };
	} catch (error) {
	  return {
		success: false,
		error: error.message
	  };
	}
  }
  
  async followUser(did) {
	if (!this.agent.session) {
	  return {
		success: false,
		error: 'Not logged in'
	  };
	}
  
	try {
	  await this.agent.follow(did);
	  return { success: true };
	} catch (error) {
	  return {
		success: false,
		error: error.message
	  };
	}
  }
  
  async unfollowUser(did) {
	if (!this.agent.session) {
	  return {
		success: false,
		error: 'Not logged in'
	  };
	}
  
	try {
	  await this.agent.deleteFollow(did);
	  return { success: true };
	} catch (error) {
	  return {
		success: false,
		error: error.message
	  };
	}
  }

  async getPost(handle, postId) {
    try {
      // Create public session if needed
      if (!this.agent.session) {
        await this.createPublicSession();
      }

      // Construct the post URI
      const postUri = `at://${handle}/app.bsky.feed.post/${postId}`;
  
	  const response = await this.agent.getPostThread({
		uri: postUri,
		depth: 1
	  });
  
	  if (!response?.data?.thread?.post) {
		throw new Error('Post not found');
	  }
  
	  // Format the post data to match your expected structure
	  const post = response.data.thread.post;
	  return {
		success: true,
		data: {
		  post: {
			uri: post.uri,
			cid: post.cid,
			author: post.author,
			record: {
			  text: post.record.text,
			  createdAt: post.record.createdAt,
			  embed: post.record.embed
			},
			embed: post.embed,
			replyCount: post.replyCount,
			repostCount: post.repostCount,
			likeCount: post.likeCount,
			viewer: post.viewer || {}
		  }
		}
	  };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }
  
  async getReplies(postUri) {
	try {
	  const response = await this.agent.getPostThread({
		uri: postUri,
		depth: 1,
	  });
	  
	  // Extract replies from thread
	  const replies = response.data.thread.replies || [];
	  
	  return {
		success: true,
		data: replies
	  };
	} catch (error) {
	  return {
		success: false,
		error: error.message
	  };
	}
  }
    
  async getProfile(did) {
    try {
      if (!this.agent.session) {
        await this.createPublicSession();
      }

      const response = await this.agent.getProfile({
        actor: did
      });

      return {
		success: true,
		data: {
		  did: response.data.did,
		  handle: response.data.handle,
		  displayName: response.data.displayName,
		  description: response.data.description,
		  avatar: response.data.avatar,
		  banner: response.data.banner,
		  followersCount: response.data.followersCount,
		  followsCount: response.data.followsCount,
		  postsCount: response.data.postsCount
		}
	  };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }
  
  async getUserPosts(did, limit = 50) {
    try {
      if (!this.agent.session) {
        await this.createPublicSession();
      }

      const response = await this.agent.getAuthorFeed({
        actor: did,
        limit: limit
      });

      if (!response.data.feed) {
        throw new Error('No feed data in response');
      }
  
	  return {
		success: true,
		data: response.data.feed.map(item => ({
		  uri: item.post.uri,
		  cid: item.post.cid,
		  author: item.post.author,
		  record: item.post.record,
		  indexedAt: item.post.indexedAt,
		  likeCount: item.post.likeCount,
		  repostCount: item.post.repostCount,
		  replyCount: item.post.replyCount,
		  viewer: item.post.viewer
		}))
	  };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Create reply with RichText support
  async createReply(parentUri, parentCid, text) {
    if (!this.agent.session) {
      return {
        success: false,
        error: 'Not logged in'
      };
    }

    try {
      // Use RichText to detect and create facets
      const richText = new RichText({ text });
      await richText.detectFacets(this.agent);

      const response = await this.agent.post({
        text: richText.text,
        facets: richText.facets,
        reply: {
          root: {
            uri: parentUri,
            cid: parentCid
          },
          parent: {
            uri: parentUri,
            cid: parentCid
          }
        }
      });

      return {
        success: true,
        data: response
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }
}